import { useCallback, useRef, useState } from 'react';
import { ChevronRight } from 'react-feather';
import styled from '@emotion/styled';
import { Popover, Tooltip } from '@mui/material';
import type { MessageTestingCell, Score } from '@trinity/chat/types/message-testing';
import { SyntheticChip } from '@trinity/chat-components/components/presentation/SectionChip';

// ─── Constants ──────────────────────────────────────────────────────────────

export const COLORS = {
  green: { text: '#2DA127', bg: '#ecfdf5', border: '#a7f3d0', accent: '#34d399' },
  yellow: { text: '#ffa700', bg: '#fefce8', border: '#fde68a' },
  red: { text: '#dc2626', bg: '#fef2f2', border: '#fecaca', accent: '#f87171', dark: '#991b1b' },
  neutral: { text: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
} as const;

export const SCORE_LABELS: { key: keyof MessageTestingCell['scores']; label: string }[] = [
  { key: 'clarity', label: 'Clarity' },
  { key: 'relevance', label: 'Relevance' },
  { key: 'differentiating', label: 'Differentiating' },
  { key: 'motivating', label: 'Motivating' },
  { key: 'credibility', label: 'Credibility' },
];

// ─── Color helpers ──────────────────────────────────────────────────────────

export function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

type ScoreBand = 'green' | 'yellow' | 'red';

function scoreBand(score: number): ScoreBand {
  if (score >= 67) return 'green';
  if (score <= 34) return 'red';
  return 'yellow';
}

/**
 * Compute the overall color band for a cell, taking sub-score distribution
 * into account when the average falls in the ambiguous middle range (35–66).
 *
 * Each sub-score contributes a weight based on its band relative to 50:
 *   >= 65  → +2  (strong green signal)
 *   50–64  → +1  (lean green)
 *   35–49  → −1  (lean red)
 *   < 35   → −2  (strong red signal)
 *
 * With 5 sub-scores the sum ranges from −10 to +10.
 *   sum >= 4   → green
 *   sum <= −4  → red
 *   otherwise  → yellow
 */
export function overallScoreBand(scores: MessageTestingCell['scores']): ScoreBand {
  const avg = computeOverallScore(scores);
  if (avg >= 67) return 'green';
  if (avg <= 34) return 'red';

  let weight = 0;
  for (const s of Object.values(scores)) {
    const v = s.value;
    if (v >= 65) weight += 2;
    else if (v >= 50) weight += 1;
    else if (v >= 35) weight -= 1;
    else weight -= 2;
  }

  if (weight >= 3) return 'green';
  if (weight <= -3) return 'red';
  return 'yellow';
}

export function scoreColor(score: number): string {
  return COLORS[scoreBand(score)].text;
}

export function scoreBg(score: number): string {
  return COLORS[scoreBand(score)].bg;
}

export function scoreBorder(score: number): string {
  return COLORS[scoreBand(score)].border;
}

export function bandColor(band: ScoreBand): string {
  return COLORS[band].text;
}

export function bandBg(band: ScoreBand): string {
  return COLORS[band].bg;
}

export function bandBorder(band: ScoreBand): string {
  return COLORS[band].border;
}

function impactBorderColor(impact: string): string {
  switch (impact) {
    case 'positive':
      return COLORS.green.accent;
    case 'negative':
      return COLORS.red.accent;
    default:
      return COLORS.neutral.border;
  }
}

export function computeOverallScore(scores: MessageTestingCell['scores']): number {
  const values = Object.values(scores).map((s: Score) => s.value);
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

// ─── Shared Cell Detail Content ─────────────────────────────────────────────

/** Renders the full detail breakdown for a single cell. Shared by the popover and drill-in views. */
export function CellDetailContent({ cell }: { cell: MessageTestingCell }): React.JSX.Element {
  const overall = computeOverallScore(cell.scores);
  const band = overallScoreBand(cell.scores);
  return (
    <>
      {/* Header */}
      <PopoverHeader>
        <PopoverVerdict $verdict={cell.overallAssessment.verdict}>
          {toTitleCase(cell.overallAssessment.verdict.replace(/_/g, ' '))}
        </PopoverVerdict>
        <PopoverScoreGroup>
          <PopoverCircleBand $band={band} />
          <PopoverScoreValueBand $band={band}>({overall})</PopoverScoreValueBand>
        </PopoverScoreGroup>
      </PopoverHeader>
      <PopoverSummary>{cell.overallAssessment.oneLineSummary}</PopoverSummary>

      {/* Strengths & Weaknesses */}
      <PopoverSection>
        <PopoverCallout $type="strength">
          <strong>Top Strength:</strong> {cell.overallAssessment.topStrength}
        </PopoverCallout>
        <PopoverCallout $type="weakness">
          <strong>Top Weakness:</strong> {cell.overallAssessment.topWeakness}
        </PopoverCallout>
      </PopoverSection>

      {/* Scores Grid */}
      <PopoverSectionTitle>Scores</PopoverSectionTitle>
      <ScoresColumns>
        {SCORE_LABELS.map(({ key, label }) => (
          <ScoreItemPopover key={key} label={label} score={cell.scores[key]} />
        ))}
      </ScoresColumns>

      {/* Improvement Suggestions */}
      {cell.improvementSuggestions.length > 0 && (
        <CollapsibleSection open>
          <CollapsibleSummary>
            Suggested Improvements ({cell.improvementSuggestions.length})
          </CollapsibleSummary>
          <PopoverSection style={{ paddingTop: 8 }}>
            {cell.improvementSuggestions.map((sug, i) => (
              <PopoverDetail key={i}>
                <strong style={{ textDecoration: 'underline' }}>
                  {toTitleCase(sug.predictedScoreLift)} Impact:
                </strong>{' '}
                {sug.change}
                <br />
                <span style={{ color: '#888', fontSize: '12px' }}>{sug.rationale}</span>
              </PopoverDetail>
            ))}
          </PopoverSection>
        </CollapsibleSection>
      )}
    </>
  );
}

// ─── Score Badge ────────────────────────────────────────────────────────────

interface ScoreBadgeProps {
  cell: MessageTestingCell;
  /** When provided, clicking the badge triggers a drill-in instead of opening the popover. */
  onDrillIn?: () => void;
}

export function ScoreBadge({ cell, onDrillIn }: ScoreBadgeProps): React.JSX.Element {
  const overall = computeOverallScore(cell.scores);
  const band = overallScoreBand(cell.scores);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleClick = useCallback(() => {
    if (onDrillIn) {
      onDrillIn();
    } else {
      setPopoverOpen(true);
    }
  }, [onDrillIn]);

  const handlePopoverClose = useCallback(() => setPopoverOpen(false), []);

  const verdictLabel = toTitleCase(cell.overallAssessment.verdict.replace(/_/g, ' '));

  return (
    <>
      <Tooltip title={onDrillIn ? `${verdictLabel} (${overall})` : ''} enterDelay={300} placement="top">
        <BadgeContainerBand $band={band} ref={anchorRef} onClick={handleClick}>
          <BadgeCircleBand $band={band} />
          <BadgeScoreLabel $score={overall}>({overall})</BadgeScoreLabel>
        </BadgeContainerBand>
      </Tooltip>
      {!onDrillIn && (
        <Popover
          open={popoverOpen}
          anchorEl={anchorRef.current}
          onClose={handlePopoverClose}
          anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
          transformOrigin={{ vertical: 'center', horizontal: 'center' }}
          marginThreshold={16}
          slotProps={{
            paper: {
              sx: {
                maxWidth: 520,
                maxHeight: 'calc(100vh - 32px)',
                overflow: 'auto',
                borderRadius: '10px',
              },
            },
          }}
        >
          <PopoverContent>
            <CellDetailContent cell={cell} />
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}

// ─── Score Item Popover ─────────────────────────────────────────────────────

function ScoreItemPopover({ label, score }: { label: string; score: Score }): React.JSX.Element {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <ScoreCompactItem ref={anchorRef} onClick={handleOpen} style={{ cursor: 'pointer' }}>
        <ScoreCompactLabel>{label}</ScoreCompactLabel>
        <ScoreCompactRight>
          <ScoreCompactValue $score={score.value}>({score.value})</ScoreCompactValue>
          <ScoreCompactDot $score={score.value} />
        </ScoreCompactRight>
      </ScoreCompactItem>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 360,
              borderRadius: '10px',
            },
          },
        }}
      >
        <ScorePopoverContent>
          <ScorePopoverHeader>
            <ScorePopoverLabel>{label}</ScorePopoverLabel>
            <ScorePopoverGroup>
              <ScorePopoverDot $score={score.value} />
              <ScorePopoverValue $score={score.value}>({score.value})</ScorePopoverValue>
            </ScorePopoverGroup>
          </ScorePopoverHeader>

          {/* <ScoreConfidence>
            <strong>Confidence:</strong>{' '}
            {score.confidence >= 0.7 ? 'High' : score.confidence >= 0.4 ? 'Medium' : 'Low'}
          </ScoreConfidence> */}

          <ScoreReasoning>{score.reasoning}</ScoreReasoning>

          {score.evidence.length > 0 && (
            <>
              <ScorePopoverSectionTitle>Evidence</ScorePopoverSectionTitle>
              {score.evidence.map((e, i) => (
                <EvidenceItem key={i} $impact={e.impact}>
                  <EvidenceQuote>&ldquo;{e.quote}&rdquo;</EvidenceQuote>
                  <EvidenceMeta>
                    <EvidenceSource>{e.source.replace(/([A-Z])/g, ' $1').trim()}</EvidenceSource>
                    <SyntheticChipInline>
                      <SyntheticChip />
                    </SyntheticChipInline>
                  </EvidenceMeta>
                </EvidenceItem>
              ))}
            </>
          )}

          {score.personaAttributesUsed.length > 0 && (
            <>
              <ScorePopoverSectionTitle>Persona Attributes</ScorePopoverSectionTitle>
              <ChipList>
                {score.personaAttributesUsed.map((attr) => (
                  <AttributeChip key={attr} $type="invoked">
                    {toTitleCase(attr)}
                  </AttributeChip>
                ))}
              </ChipList>
            </>
          )}
        </ScorePopoverContent>
      </Popover>
    </>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const _BadgeContainer = styled.div<{ $score: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  min-height: 48px;
  cursor: pointer;
  background: ${(p) => scoreBg(p.$score)};
  border-radius: 4px;
  margin: 4px;
  border: 1px solid ${(p) => scoreBorder(p.$score)};
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 0 0 2px ${(p) => scoreBorder(p.$score)};
  }
`;

const _BadgeCircle = styled.div<{ $score: number }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${(p) => scoreColor(p.$score)};
`;

const BadgeContainerBand = styled.div<{ $band: ScoreBand }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  min-height: 48px;
  cursor: pointer;
  background: ${(p) => bandBg(p.$band)};
  border-radius: 4px;
  margin: 4px;
  border: 1px solid ${(p) => bandBorder(p.$band)};
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 0 0 2px ${(p) => bandBorder(p.$band)};
  }
`;

const BadgeCircleBand = styled.div<{ $band: ScoreBand }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${(p) => bandColor(p.$band)};
`;

const BadgeScoreLabel = styled.div<{ $score: number }>`
  font-size: 9px;
  font-weight: 600;
  margin-top: 2px;
`;

const _BadgeHint = styled.div`
  font-size: 9px;
  letter-spacing: 0.3px;
  margin-top: 4px;
  color: #999;
`;

const PopoverContent = styled.div`
  padding: 16px;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const PopoverVerdict = styled.div<{ $verdict: string }>`
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const _PopoverCircle = styled.div<{ $score: number }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(p) => scoreColor(p.$score)};
`;

const PopoverCircleBand = styled.div<{ $band: ScoreBand }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(p) => bandColor(p.$band)};
`;

const PopoverScoreGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const _PopoverScoreValue = styled.span<{ $score: number }>`
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => scoreColor(p.$score)};
`;

const PopoverScoreValueBand = styled.span<{ $band: ScoreBand }>`
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => bandColor(p.$band)};
`;

const PopoverSummary = styled.div`
  font-size: 14px;
  color: #555;
  line-height: 1.4;
  margin-bottom: 12px;
`;

const PopoverSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: var(--black-l);
  margin: 12px 0 6px;
  padding-top: 8px;
  border-top: 1px solid #eee;
`;

const PopoverSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PopoverCallout = styled.div<{ $type: 'strength' | 'weakness' }>`
  font-size: 13px;
  line-height: 1.4;
  padding: 6px 8px;
  border-radius: 4px;
  background: ${(p) => (p.$type === 'strength' ? COLORS.green.bg : COLORS.red.bg)};

  strong {
    font-weight: 600;
  }
`;

const PopoverDetail = styled.div`
  font-size: 13px;
  color: #555;
  line-height: 1.4;

  strong {
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const ScoresColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 12px;
`;

const ScoreCompactLabel = styled.div`
  font-size: 12px;
  color: #666;
  transition: color 0.1s ease;
`;

const ScoreCompactValue = styled.span<{ $score: number }>`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => scoreColor(p.$score)};
  opacity: 0;
  transition: opacity 0.1s ease;
`;

const ScoreCompactRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ScoreCompactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1px 0;

  &:hover ${ScoreCompactLabel} {
    color: var(--hyperlink);
    text-decoration: underline;
  }

  &:hover ${ScoreCompactValue} {
    opacity: 1;
  }
`;

const ScoreCompactDot = styled.div<{ $score: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => scoreColor(p.$score)};
`;

const ScorePopoverContent = styled.div`
  padding: 14px;
`;

const ScorePopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const ScorePopoverLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
`;

const ScorePopoverDot = styled.div<{ $score: number }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${(p) => scoreColor(p.$score)};
`;

const ScorePopoverGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ScorePopoverValue = styled.span<{ $score: number }>`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => scoreColor(p.$score)};
`;

const _ScoreConfidence = styled.div`
  font-size: 11px;
  color: #666;
  margin-bottom: 6px;

  strong {
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const ScoreReasoning = styled.div`
  font-size: 11px;
  color: #555;
  line-height: 1.45;
  margin-bottom: 4px;
`;

const ScorePopoverSectionTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #999;
  margin: 10px 0 5px;
  padding-top: 6px;
  border-top: 1px solid #eee;
`;

const EvidenceItem = styled.div<{ $impact: string }>`
  padding: 5px 8px;
  margin: 3px 0;
  border-left: 3px solid ${(p) => impactBorderColor(p.$impact)};
  background: #fafafa;
  border-radius: 0 4px 4px 0;
`;

const EvidenceQuote = styled.div`
  font-size: 11px;
  font-style: italic;
  color: #333;
  line-height: 1.4;
`;

const EvidenceMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
`;

const EvidenceSource = styled.span`
  font-size: 9px;
  color: #888;
  text-transform: capitalize;
`;

const SyntheticChipInline = styled.span`
  display: inline-flex;

  & > span {
    margin: 0;
    font-size: 9px;
    padding: 0 6px;
    line-height: 16px;
  }
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const AttributeChip = styled.span<{ $type: 'invoked' | 'unused' }>`
  font-size: 10px;
  padding: 4px 6px;
  border-radius: 10px;
  background: ${(p) => (p.$type === 'invoked' ? 'rgb(229, 245, 252)' : COLORS.neutral.bg)};
  color: ${(p) => (p.$type === 'invoked' ? 'var(--black-l)' : COLORS.neutral.text)};
`;

const CollapsibleSection = styled.details`
  margin-top: 8px;
`;

const StyledSummary = styled.summary`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #999;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;
  display: flex;
  align-items: center;
  list-style: none;
  border-bottom: 1px solid #eee;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    color: #666;
  }
`;

const SummaryChevron = styled(ChevronRight)`
  margin-left: auto;
  flex-shrink: 0;
  color: #ccc;
  transition: transform 0.15s ease;

  details[open] > summary > & {
    transform: rotate(90deg);
  }
`;

function CollapsibleSummary({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <StyledSummary>
      {children}
      <SummaryChevron size={12} />
    </StyledSummary>
  );
}
